import React, { useState } from 'react'
import H from '../data'
import HI from '../components/Icons'
import { Av, Stars, StatusBadge, Delta, Kpi, Banner, Empty, SkeletonRows, BarChart, Sparkline, CardHead, money } from '../components/SharedComponents'

/* ============ CoCafe Host — Availability ============ */
function AvailabilityGrid({ grid, toggle }) {
  // grid[day][hour] = 'open' | 'closed' | 'booked'
  return (
    <div style={{ overflowX: "auto" }}>
      <div style={{ display: "grid", gridTemplateColumns: "56px repeat(7, 1fr)", gap: 4, minWidth: 620 }}>
        <div />
        {H.days.map((d) => <div key={d} className="t-xs" style={{ textAlign: "center", fontWeight: 700, color: "var(--ink-2)", paddingBottom: 4 }}>{d}</div>)}
        {H.HOURS.map((h) => (
          <React.Fragment key={h}>
            <div className="t-xs mono muted" style={{ textAlign: "right", paddingRight: 8, alignSelf: "center" }}>{H.hLabel(h)}</div>
            {H.days.map((d) => {
              const st = grid[d][h];
              const styleMap = {
                open: { background: "var(--green-weak)", color: "var(--green-ink)", border: "1px solid transparent" },
                booked: { background: "var(--green)", color: "#fff", border: "1px solid var(--green)" },
                closed: { background: "var(--surface-3)", color: "var(--faint)", border: "1px solid var(--border)" },
              };
              return (
                <button key={d + h} onClick={() => st !== "booked" && toggle(d, h)} disabled={st === "booked"}
                  title={st === "booked" ? "Booked — can't close" : st === "open" ? "Open · click to close" : "Closed · click to open"}
                  style={{ height: 26, borderRadius: 5, cursor: st === "booked" ? "default" : "pointer", fontSize: 10, fontWeight: 600, ...styleMap[st] }}>
                  {st === "booked" ? "•" : null}
                </button>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function Availability({ toast }) {
  // build grid from openHours + a few booked cells
  const booked = { Tue: [10, 11, 12], Thu: [9, 14, 15], Fri: [16, 17, 18], Sat: [10, 11] };
  const build = () => {
    const g = {};
    H.days.forEach((d) => {
      g[d] = {};
      const [o, c] = H.openHours[d];
      H.HOURS.forEach((h) => { g[d][h] = h >= o && h < c ? "open" : "closed"; });
      (booked[d] || []).forEach((h) => { if (g[d][h] === "open") g[d][h] = "booked"; });
    });
    return g;
  };
  const [grid, setGrid] = useState(build);
  const [dirty, setDirty] = useState(false);
  const [blocks, setBlocks] = useState(H.blockedDates);
  const [adding, setAdding] = useState(false);

  const toggle = (d, h) => { setGrid((g) => ({ ...g, [d]: { ...g[d], [h]: g[d][h] === "open" ? "closed" : "open" } })); setDirty(true); };

  const openCount = H.days.reduce((s, d) => s + H.HOURS.filter((h) => grid[d][h] === "open" || grid[d][h] === "booked").length, 0);

  return (
    <div className="page fade-in">
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 4, color: 'var(--ink)' }}>Manage Availability</h1>
        <p style={{ fontSize: 14, color: 'var(--muted)', margin: 0 }}>Set your opening hours, block dates, and manage your schedule</p>
      </div>

      <div className="row g-4">
        {/* weekly grid - FULL WIDTH ON MOBILE */}
        <div className="col-12">
        <div className="card">
          <CardHead title="Weekly schedule" sub={openCount + " open desk-hours per week"}
            right={<div className="row g10 t-xs muted" style={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <span className="row g4"><span style={{ width: 11, height: 11, borderRadius: 3, background: "var(--green)" }} /> Open</span>
              <span className="row g4"><span style={{ width: 11, height: 11, borderRadius: 3, background: "var(--green-weak)" }} /> Available</span>
              <span className="row g4"><span style={{ width: 11, height: 11, borderRadius: 3, background: "#e8e8e8" }} /> Closed</span>
            </div>} />
          <div className="card-pad">
            <AvailabilityGrid grid={grid} toggle={toggle} />
          </div>
          <div className="card-foot t-xs muted">Click any time slot to toggle between open and closed</div>
        </div>
        </div>

        {/* opening hours + blocked dates - TWO COLUMN SIDE-BY-SIDE - COMPACT BOXES */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
          {/* opening hours quick edit - SMALL BOX */}
          <div className="card" style={{ padding: '12px', maxWidth: '350px', width: '100%' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '8px' }}>Opening hours</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {H.days.map((d) => {
                const [o, c] = H.openHours[d];
                return (
                  <div key={d} className="row between" style={{ padding: "3px 6px", alignItems: "center", fontSize: '11px' }}>
                    <span style={{ fontWeight: 600, minWidth: 45, color: 'var(--ink)' }}>{d}</span>
                    <span style={{ fontWeight: 600, color: 'var(--green)', whiteSpace: "nowrap" }}>{H.hLabel(o)}–{H.hLabel(c)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* blocked dates - SMALL BOX, SIDE-BY-SIDE */}
          <div className="card" style={{ padding: '12px', maxWidth: '350px', width: '100%' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Blocked dates</span>
              <button className="btn btn-soft btn-sm" onClick={() => setAdding(true)} style={{ padding: '2px 6px', fontSize: '10px' }}><HI.plus s={12} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: '250px', overflowY: 'auto' }}>
              {blocks.length === 0 ? (
                <div style={{ fontSize: '11px', color: 'var(--muted)', textAlign: 'center', padding: '8px 0' }}>No blocks</div>
              ) : blocks.map((b, i) => {
                const tone = { event: "purple", maintenance: "amber", holiday: "blue" }[b.kind];
                const ic = { event: <HI.users s={12} />, maintenance: <HI.settings s={12} />, holiday: <HI.sun s={12} /> }[b.kind];
                return (
                  <div key={i} style={{ padding: "6px 8px", border: "1px solid var(--border)", borderRadius: '6px', fontSize: '11px' }}>
                    <div className="row g6" style={{ marginBottom: 3, alignItems: "flex-start" }}>
                      <span style={{ color: `var(--${tone})`, flexShrink: 0 }}>{ic}</span>
                      <div className="grow" style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '10px' }}>{b.date}</div>
                        <div style={{ fontSize: '9px', color: 'var(--muted)' }}>{b.reason}</div>
                      </div>
                    </div>
                    <button className="link" style={{ fontSize: '10px' }} onClick={() => { setBlocks((x) => x.filter((_, j) => j !== i)); toast("Block removed", b.date + " is open again"); }}>Remove</button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {adding && (
        <div className="modal">
          <div className="scrim" onClick={() => setAdding(false)} />
          <div className="modal-card">
            <div className="modal-b">
              <div className="t-h2" style={{ marginBottom: 14 }}>Block a date</div>
              <div className="col g14">
                <div className="field"><span className="lbl">Date</span><input className="inp" type="date" /></div>
                <div className="field"><span className="lbl">Type</span>
                  <select className="sel"><option>Private event — full buyout</option><option>Maintenance</option><option>Public holiday</option></select></div>
                <div className="field"><span className="lbl">Note for your records</span><input className="inp" placeholder="e.g. Birthday buyout, 20 guests" /></div>
              </div>
            </div>
            <div className="modal-f">
              <button className="btn btn-ghost" onClick={() => setAdding(false)}>Cancel</button>
              <button className="btn btn-pri" onClick={() => { setBlocks((x) => [...x, { date: "24 Jun", reason: "Private event — full buyout", kind: "event" }]); setAdding(false); toast("Date blocked", "24 Jun is now closed to bookings"); }}>Block date</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Availability;
